import type { IContainer } from '@monofe/di';
import { injectOrBuildContainer } from '@monofe/di';
import TYPES from '../util/types';

export enum IConfigLogLevel {
    Undef = 0,
    Milestone = 1,
    Trace = 2,
    Verbose = 3,
    Info = 4,
    Warn = 5,
    Error = 6,
    Fatal = 7,
    LastValue = 8,
}

export enum IConfigLogDestination {
    Undef = 0,
    Console = 1,
    /* Deprecated: Remote_ApplicationInsights = 2, */
    LocalStorage = 3,
    Remote_Sentry = 4,
    LastValue = 5,
}

/* Remove #Undef & #LastValue from IConfigLogDestination */
export type IConfigLogDestination_ValidValuesOnly = Exclude<
    IConfigLogDestination,
    IConfigLogDestination.LastValue | IConfigLogDestination.Undef
>;

export interface IConfigLogSettings {
    authorizationKey?: string;
    name?: string;
}

export interface IConfigLog {
    loggingLevelsEnabled: IConfigLogLevel[];
    outputDestinations: IConfigLogDestination[];
    outputConfiguration: Partial<Record<IConfigLogDestination_ValidValuesOnly, IConfigLogSettings>>;
}

export interface IConfigAPI {
    url: string;
    auth: {
        clientId: string;
        clientSecret: string;
        grantType: string;
        username: string;
        password: string;
    };
    extension: {
        auth: string;
        siteData: string;
        mediaDiff: string;
        media: string;
        mediaWithAccessToken: string;
    };
}

export interface IConfigManifest extends Record<string | number | symbol, unknown> {
    short_name: string;
    name: string;
    description: string;
    icons: { src: string; sizes: string; type: string }[];
    start_url: string;
    display: string;
    theme_color: string;
    background_color: string;
    orientation: string;
    categories: string[];
}

export interface IConfigCacheAssets {
    cacheName: string;
    expiration: number;
    maxEntries: number;
    matcher: RegExp;
    ignoreSearch?: boolean;
    cacheQueryOptions?: Partial<CacheQueryOptions>;
    fetchOptions?: Partial<RequestInit>;
}

export interface IConfigCacheOAuthToken {
    tokenPath: string;
}

export function isConfigCacheAssets(object: unknown): object is IConfigCacheAssets {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IConfigCacheAssets> = object as Partial<IConfigCacheAssets>;

    return (
        !isNullOrUndefined(record.cacheName) &&
        !isNullOrUndefined(record.expiration) &&
        !isNullOrUndefined(record.maxEntries) &&
        !isNullOrUndefined(record.matcher)
    );
}

export function isConfigCacheOAuthToken(object: unknown): object is IConfigCacheOAuthToken {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IConfigCacheOAuthToken> = object as Partial<IConfigCacheOAuthToken>;

    return !isNullOrUndefined(record.tokenPath);
}

export type IConfigCache = (IConfigCacheAssets | IConfigCacheOAuthToken)[];

export function injectConfigLog(container: IContainer = injectOrBuildContainer()): IConfigLog {
    return container.get<IConfigLog>(TYPES.IConfigLog);
}

export function bindConfigLog(val: IConfigLog, container: IContainer = injectOrBuildContainer()) {
    container.bind<IConfigLog>(TYPES.IConfigLog, val);
}

export function unbindConfigLog(container: IContainer = injectOrBuildContainer()): void {
    container.remove(TYPES.IConfigLog);
}

export function injectConfigAPI(container: IContainer = injectOrBuildContainer()): IConfigAPI {
    return container.get<IConfigAPI>(TYPES.IConfigAPI);
}

export function bindConfigAPI(
    val: IConfigAPI,
    container: IContainer = injectOrBuildContainer(),
): void {
    container.bind<IConfigAPI>(TYPES.IConfigAPI, val);
}

export function unbindConfigAPI(container: IContainer = injectOrBuildContainer()): void {
    container.remove(TYPES.IConfigAPI);
}

export function injectConfigManifest(
    container: IContainer = injectOrBuildContainer(),
): IConfigManifest {
    return container.get<IConfigManifest>(TYPES.IConfigManifest);
}

export function bindConfigManifest(
    val: IConfigManifest,
    container: IContainer = injectOrBuildContainer(),
): void {
    container.bind<IConfigManifest>(TYPES.IConfigManifest, val);
}

export function unbindConfigManifest(container: IContainer = injectOrBuildContainer()): void {
    container.remove(TYPES.IConfigManifest);
}

export function injectConfigCache(container: IContainer = injectOrBuildContainer()): IConfigCache {
    return container.get<IConfigCache>(TYPES.IConfigCache);
}

export function bindConfigCache(
    val: IConfigCache,
    container: IContainer = injectOrBuildContainer(),
): void {
    container.bind<IConfigCache>(TYPES.IConfigCache, val);
}

export function unbindConfigCache(container: IContainer = injectOrBuildContainer()): void {
    container.remove(TYPES.IConfigCache);
}
